import {colors} from '@styles/colors';
import {css} from '@emotion/react';
import {fontWeight} from '@styles/helpers';
import {HEX2RGB} from '@utils/colorTools';

/**
 * Very important: also add here - emotions.d.ts
 */
export interface IAttenderTheme {
  name: ThemeName;
  testColor: string;
  pillBorderRadius: number;
  selectionBorderSize: number;
  timelineTopPadding: number;
  layout: ILayout;
  fontSizes: IFontSizes;
  colors: IThemeColors;
  weights: IFontWeights;
}

export enum ThemeName {
  Default = 'default',
  Microsoft = 'microsoft',
}

export enum MenuType {
  Side = 'side',
  Top = 'top',
}

export interface ILayout {
  shadowOpacity: number;

  smallSpace: number;
  mediumSpace: number;
  largeSpace: number;
  extraLargeSpace: number;
  hugeSpace: number;

  headerHeight: number;

  menuType: MenuType;
}

export interface IFontSizes {
  tiny: number;
  small: number;
  medium: number;
  large: number;
}

export interface IFontWeights {
  light: number;
  regular: number;
  medium: number;
  semiBold: number;
  bold: number;
}

export interface IThemeColors {
  tutorial: string;
  progressBarPrimary: string; // bar when doing registry
  progressBarSecondary: string; // background of the registry bar

  mainTextColor: string; // the primary site color
  placeholderColor: string; // use for inpout fields

  selectionBorderColor: string; // color for grid selections

  // buttons states
  buttonBorderColor: string;
  buttonHoverColor: string;
  buttonColor: string;

  shadowColor: string; // default shadow color
  mockColor: string; // background color of mock items while loading
  fadedLineColor: string;
  pillColor: string; // pill of the timeline bar

  backgroundColor: string; // main background color

  yellow: string;
  yellowDark: string;
  orange: string;
  white: string;
  red: string;
  redText: string;
  offWhite: string;
  white_rgb: string;
  blue_rgb: string;
  blue_50: string;
  faded_blue: string;
  light_blue: string;
  faded_white: string;
  green: string;
  black: string;
  gray: string;
  gray_50: string;
  gray_20: string;
  gray_10: string;
  darkenedRed: string;
  darkenedYellow: string;
  darkenedBlue: string;
}

const standardTheme: IAttenderTheme = {
  name: ThemeName.Default,

  testColor: 'red',
  pillBorderRadius: 13,
  selectionBorderSize: 1.5,
  timelineTopPadding: 30,
  layout: {
    menuType: MenuType.Side,

    shadowOpacity: 0.02,
    smallSpace: 10,
    mediumSpace: 20,
    largeSpace: 30,
    extraLargeSpace: 45,
    hugeSpace: 60,

    headerHeight: 76,
  },
  fontSizes: {
    tiny: 12,
    small: 14,
    medium: 16,
    large: 30,
  },
  colors: {
    tutorial:'#2A848E',
    progressBarPrimary: '#ffffff',
    progressBarSecondary: '#000000',
    mainTextColor: '#277982',
    placeholderColor: '#277982',
    selectionBorderColor: '#666666',

    buttonBorderColor: '#D1D1D1',
    buttonHoverColor: '#639EA5',
    buttonColor: 'white',

    shadowColor: 'rgba(0, 0, 0, .05)',
    mockColor: 'rgba(0, 0, 0, .05)',
    fadedLineColor: 'rgba(112, 112, 112, .2)',
    pillColor: '#2A848E',

    backgroundColor: '#f5f5f5',

    yellow: '#ffd31a',
    yellowDark: '#ffc745',
    orange: '#f0711f',
    white: '#ffffff',
    red: '#fb4856',
    redText: '#D01121',
    offWhite: '#f5f5f5',
    // blue: '#2a848e',
    white_rgb: '255,255,255',
    blue_rgb: '39, 121, 130',
    blue_50: 'rgba(39, 121, 130,.5) ',
    faded_blue: '#707070',
    light_blue: '#72ACB3',
    faded_white: '#F7F7F7',
    green: '#299b27',
    black: '#000',
    gray: '#707070',
    gray_50: 'rgba(112, 112, 112, .5)',
    gray_20: 'rgba(112, 112, 112, .2)',
    gray_10: 'rgba(186, 186, 186, .1)',
    darkenedRed: '#64020a',
    darkenedYellow: '#806600',
    darkenedBlue: '#42757b',
  },
  weights: {
    light: 300,
    regular: 400,
    medium: 500,
    semiBold: 600,
    bold: 700,
  },
};

const microsoftTheme: IAttenderTheme = {
  name: ThemeName.Microsoft,

  testColor: 'red',
  pillBorderRadius: 5,
  selectionBorderSize: 1.5,
  timelineTopPadding: 10,

  layout: {
    menuType: MenuType.Side,
    shadowOpacity: 0.02,

    smallSpace: 10,
    mediumSpace: 20,
    largeSpace: 30,
    extraLargeSpace: 45,
    hugeSpace: 60,

    headerHeight: 56,
  },
  fontSizes: {
    tiny: 12,
    small: 14,
    medium: 16,
    large: 30,
  },
  colors: {
    tutorial:'#6264A7',
    progressBarPrimary: '#FB4856',
    progressBarSecondary: '#f8a7ad',
    mainTextColor: '#000000',
    placeholderColor: '#277982',
    selectionBorderColor: '#666666',

    buttonBorderColor: '#D1D1D1',
    buttonColor: 'white',
    buttonHoverColor: '#3A97A3',
    
    shadowColor: 'rgba(0, 0, 0, .05)',
    mockColor: 'rgba(0, 0, 0, .05)',
    fadedLineColor: 'rgba(112, 112, 112, .2)',
    pillColor: '#2A848E',

    backgroundColor: '#f5f5f5',

    yellow: '#ffd31a',
    yellowDark: '#ffc745',
    orange: '#f0711f',
    white: '#ffffff',
    red: '#fb4856',
    redText: '#D01121',
    offWhite: '#f5f5f5',
    // blue: '#2a848e',
    white_rgb: '255,255,255',
    blue_rgb: '39, 121, 130',
    blue_50: 'rgba(39, 121, 130, .5) ',
    faded_blue: '#707070',
    light_blue: '#72ACB3',
    faded_white: '#F7F7F7',
    green: '#299b27',
    black: '#000',
    gray: '#707070',
    gray_50: 'rgba(112, 112, 112, .5)',
    gray_20: 'rgba(112, 112, 112, .2)',
    gray_10: 'rgba(186, 186, 186, .1)',
    darkenedRed: '#64020a',
    darkenedYellow: '#806600',
    darkenedBlue: '#42757b',
  },
  weights: {
    light: 300,
    regular: 400,
    medium: 500,
    semiBold: 600,
    bold: 700,
  },
};

const attenderTheme = microsoftTheme;
export default attenderTheme;

/**
 * Lagacy - get rid of this
 */
interface IStudentThemeColors {
  unknown: any;
  attending: any;
  missing: any;
  late: any;
}

export const studentThemeColors: IStudentThemeColors = {
  unknown: css`
    --textColor: ${colors.mainTextColor};
    --backgroundColor: ${colors.white};
    --borderColor: ${colors.mainTextColor};
  `,
  attending: css`
    --textColor: ${colors.white};
    --backgroundColor: ${colors.mainTextColor};
    --borderColor: ${colors.mainTextColor};
  `,
  missing: css`
    --textColor: ${colors.white};
    --backgroundColor: ${colors.red};
    --borderColor: ${colors.red};
  `,
  late: css`
    --textColor: ${colors.white};
    --backgroundColor: ${colors.yellow};
    --borderColor: ${colors.yellow};
  `,
};

export const studentThemeContrastColors: IStudentThemeColors = {
  unknown: css`
    --textColor: ${colors.mainTextColor};
    --backgroundColor: ${`rgba(255,255,255,0.9)`};
    --borderColor: ${colors.gray};
  `,
  attending: css`
    --textColor: ${colors.mainTextColor};
    --backgroundColor: ${`rgba(255,255,255,0.9)`};
    --borderColor: ${colors.gray};
  `,
  missing: css`
    --textColor: ${colors.mainTextColor};
    --backgroundColor: ${`rgba(255,255,255,0.9)`};
    --borderColor: ${colors.gray};
  `,
  late: css`
    --textColor: ${colors.mainTextColor};
    --backgroundColor: ${`rgba(255,255,255,0.9)`};
    --borderColor: ${colors.gray};
  `,
};

export const studentGridThemeColors: IStudentThemeColors = {
  unknown: css`
    --textColor: ${colors.mainTextColor};
    --backgroundColor: ${`rgba(255,255,255,0.9)`};
    --borderColor: ${colors.gray};
  `,
  attending: css`
    --textColor: ${colors.white};
    --backgroundColor: transparent linear-gradient(0deg, #50b0ba 0%, #2a848e 100%) 0% 0% no-repeat padding-box;
    --borderColor: ${colors.gray};
  `,
  missing: css`
    --textColor: ${colors.white};
    --backgroundColor: transparent linear-gradient(0deg, ${colors.red} 0%, #de0012 100%) 0% 0% no-repeat padding-box;
    --borderColor: ${colors.gray};
  `,
  late: css`
    --textColor: ${colors.white};
    --backgroundColor: transparent linear-gradient(0deg, #c7861c 0%, #ffd112 100%) 0% 0% no-repeat padding-box;
    --borderColor: ${colors.gray};
  `,
};

export const ListThemeContrast: IStudentThemeColors = {
  unknown: css`
    --backgroundColor: ${colors.white};
  `,
  attending: css`
    --backgroundColor: ${colors.white};
  `,
  missing: css`
    --backgroundColor: ${colors.white};
  `,
  late: css`
    --backgroundColor: ${colors.white};
  `,
};

export const ListThemeColors: IStudentThemeColors = {
  unknown: css`
    --backgroundColor: ${colors.white};
  `,
  attending: css`
    --backgroundColor: rgba(${HEX2RGB(colors.mainTextColor)}, 0.95);
  `,
  missing: css`
    --backgroundColor: rgba(${HEX2RGB(colors.red)}, 0.95);
  `,
  late: css`
    --backgroundColor: rgba(${HEX2RGB(colors.yellow)}, 0.95);
  `,
};

export const classThemeColors: IStudentThemeColors = {
  unknown: css`
    --textColor: ${colors.gray};
    --fontWeight: ${fontWeight.regular};
  `,
  attending: css`
    --textColor: ${colors.mainTextColor};
    --fontWeight: ${fontWeight.bold};
  `,
  missing: css`
    --textColor: ${colors.red};
    --fontWeight: ${fontWeight.bold};
  `,
  late: css`
    --textColor: ${colors.orange};
    --fontWeight: ${fontWeight.bold};
  `,
};

export const attendanceFill: IStudentThemeColors = {
  unknown: css`
    --fill: ${colors.faded_blue};
  `,
  attending: css`
    --fill: ${colors.mainTextColor};
  `,
  missing: css`
    --fill: ${colors.red};
  `,
  late: css`
    --fill: ${colors.yellow};
  `,
};


export const SwipeThemeColors: IStudentThemeColors = {
  unknown: {background:`rgba(255,255,255,0.9)`, color:`#000`},
  attending: {background:`transparent linear-gradient(0deg, #50b0ba 0%, #2a848e 100%) 0% 0% no-repeat padding-box`, color:`${colors.white}`},
  missing:{background:`transparent linear-gradient(0deg, ${colors.red} 0%, #de0012 100%) 0% 0% no-repeat padding-box`, color:`${colors.white}`},
  late: {background:`linear-gradient(0deg, #c7861c 0%, #ffd112 100%) 0% 0% no-repeat padding-box`, color:`${colors.white}`}
};